import React, { FC, ReactNode } from "react"
import { graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import xss from "xss"

import { PrefooterFragmentFragment } from "@graphqlTypes"
import { convertStringToKebabCase, getImgixImageData, oneLineHelpScout } from "@utils"

import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "@components/Button/Button"
import HubspotForm from "@components/HubspotForm/HubspotForm"

import "./Prefooter.scss"
import MobileAppButtons from "@components/MobileAppButtons/MobileAppButtons"

type PrefooterProps = {
  children?: ReactNode,
}

const Prefooter: FC<PrefooterFragmentFragment & PrefooterProps> = ({
  backgroundCss,
  backgroundImageLeft,
  backgroundImageRight,
  bodyText,
  button1Label,
  button1Url,
  button2Label,
  button2Url,
  children,
  hubspotFormData,
  includeMobileAppButtons,
  formRedirectUrl,
  title,
  useDarkMode,
}) => {
  return (
    <div
      className={`Prefooter${useDarkMode ? " use-dark-mode" : ""}`}
      style={backgroundCss ? {
        background: backgroundCss,
      } : {}}
    >
      {backgroundImageLeft?.imgixImage?.gatsbyImageData && backgroundImageRight?.imgixImage?.gatsbyImageData ? (
        <>
          <GatsbyImage
            alt=""
            className="bg-left"
            image={backgroundImageLeft.imgixImage.gatsbyImageData}
            role="presentation"
          />
          <GatsbyImage
            alt=""
            className="bg-right"
            image={backgroundImageRight.imgixImage.gatsbyImageData}
            role="presentation"
          />
        </>
      ) : (
        <>
          <GatsbyImage
            alt=""
            className="bg-left"
            image={
              getImgixImageData(
                "/images/components/Prefooter/Prefooter--bg-left.png",
                616,
                3 / 4
              )}
            role="presentation"
          />
          <GatsbyImage
            alt=""
            className="bg-right"
            image={getImgixImageData(
              "/images/components/Prefooter/Prefooter--bg-right.png",
              616,
              3 / 4
            )}
            role="presentation"
          />
        </>
      )
      }
      <div className="container">
        <div className="grid">
          <div className="content column column--2-12-M">
            {title && (
              <h2
                className="type--headline-medium-5XL"
                dangerouslySetInnerHTML={{ __html: xss(oneLineHelpScout(title)) }}
                id={convertStringToKebabCase(title)}
              />
            )}
            {bodyText?.bodyText && <p className="type--text-L max-width--column--6" dangerouslySetInnerHTML={{ __html: xss(bodyText?.bodyText) }} />}
            {!hubspotFormData && (
              <div className="Prefooter--Buttons">
                {
                  /*
                    If formId is unpopulated and formRedirectUrl is populated,
                    assume the intention is a CTA to an ungated PDF (ebook) asset. 
                    An example of this case is seen in CMS Posts with prefooter="Download"
                  */
                }
                {((button1Label && button1Url) || formRedirectUrl) && (
                  <Button
                    href={button1Url || formRedirectUrl || "/"}
                    size={BUTTON_SIZES.L}
                  >
                    {button1Label || "Download the ebook"}
                  </Button>
                )}
                {button2Label && button2Url && (
                  <Button
                    href={button2Url}
                    size={BUTTON_SIZES.L}
                    variant={BUTTON_VARIANTS.SECONDARY}
                  >
                    {button2Label}
                  </Button>
                )}
              </div>
            )}
            {hubspotFormData && (
              <HubspotForm
                {...hubspotFormData}
                redirectUrl={formRedirectUrl}
              />
            )}
            {includeMobileAppButtons && <MobileAppButtons />}
            {children && children}
          </div>
        </div>
      </div>
    </div >
  )
}

export const PrefooterFragment = graphql`
  fragment PrefooterFragment on ContentfulPrefooter {
    backgroundCss
    backgroundImageLeft {
      imgixImage {
        gatsbyImageData(width: 616)
      }
    }
    backgroundImageRight {
      imgixImage {
        gatsbyImageData(width: 616)
      }
    }
    bodyText {
      bodyText
    }
    button1Label
    button1Url
    button2Label
    button2Url
    hubspotFormData {
      ...HubspotFormFragment
    }
    includeMobileAppButtons
    formRedirectUrl
    title
    useDarkMode
  }
`

export default Prefooter
